import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { TimeSelect } from '../../components/ui/TimeSelect';
import { ArrowLeft, Heart, FileText, Check, X, RotateCcw, Loader2, Plus, ChevronDown, ChevronRight, Pencil, Clock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { formatSoldAs } from '../../components/sales/saleConstants';
import { useConfirm } from '../../components/common/ConfirmDialog';

const CardViewPage = () => {
  const { isViewOnly } = useAuth();
  const { saleId, cardId } = useParams();
  const [searchParams] = useSearchParams();
  const shouldRun = searchParams.get('run') === 'true';
  return (
    <SidebarLayout>
      <CardViewContent cardId={cardId} saleId={saleId} viewOnly={isViewOnly()} showBackButton shouldRun={shouldRun} />
    </SidebarLayout>
  );
};

export const CardViewContent = ({ cardId, saleId, viewOnly = false, showBackButton = false, shouldRun = false }) => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const qc = useQueryClient();

  const [addViewingOpen, setAddViewingOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isDigitalSale, setIsDigitalSale] = useState(false);

  const { data: cardData, isLoading: loading } = useQuery({
    queryKey: QK.saleCard(cardId),
    queryFn: async () => {
      const res = await api.get(`/sale-cards/${cardId}`);
      const cardResult = res.data;
      const [oppsRes, sessRes, saleRes] = await Promise.all([
        api.get(`/sale-opportunities?sale_id=${cardResult.sale_id}`),
        api.get(`/sales/${cardResult.sale_id}/sessions`),
        api.get(`/sales/${cardResult.sale_id}`),
      ]);
      if (saleRes.data?.is_digital_sale) setIsDigitalSale(true);
      if (shouldRun && cardResult.status === 'pending') {
        await api.put(`/sale-cards/${cardId}`, { status: 'in_progress' });
        cardResult.status = 'in_progress';
      }
      return { card: cardResult, opportunities: oppsRes.data || [], saleSessions: sessRes.data || [] };
    },
    enabled: !!cardId,
    onError: () => toast.error('Failed to load card'),
  });

  const card         = cardData?.card         ?? null;
  const opportunities = cardData?.opportunities ?? [];
  const saleSessions = cardData?.saleSessions  ?? [];

  const invalidateCard = () => {
    qc.invalidateQueries({ queryKey: QK.saleCard(cardId) });
    qc.invalidateQueries({ queryKey: QK.saleCards(saleId) });
  };

  const updateViewing = async (viewingId, data) => {
    try {
      await api.put(`/sale-cards/viewings/${viewingId}`, data);
      invalidateCard();
    } catch { toast.error('Failed to update'); }
  };

  const updateCardNotes = async (notes) => {
    try {
      await api.put(`/sale-cards/${cardId}`, { other_visit_notes: notes });
    } catch { /* debounced, silent */ }
  };

  const handleRequeue = async () => {
    try {
      await api.put(`/sale-cards/${cardId}`, { status: 'requeued' });
      qc.setQueryData(QK.saleCard(cardId), prev => prev ? { ...prev, card: { ...prev.card, status: 'requeued' } } : prev);
      toast.success('Card re-queued');
      navigate(-1);
    } catch { toast.error('Failed to re-queue'); }
  };

  const handleDeleteWithConfirm = async () => {
    if (!await confirm({ title: 'Are you sure you want to delete this card?', destructive: true })) return;
    setDeleting(true);
    try {
      await api.patch(`/sale-cards/${cardId}`, { status: 'deleted' });
      toast.success('Card deleted');
      navigate(-1);
    } catch {
      toast.error('Failed to delete card');
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin" /></div>
    );
  }

  if (!card) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Card not found</p>
        {showBackButton && <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>Go Back</Button>}
      </div>
    );
  }

  const viewings = card.viewings || [];
  const viewerEntity = card.viewer_entity;
  const statusBadgeMap = {
    pending: 'bg-gray-100 text-gray-700',
    in_progress: 'bg-blue-100 text-blue-700',
    complete: 'bg-green-100 text-green-700',
    requeued: 'bg-yellow-100 text-yellow-700',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-3">
          {showBackButton && (
            <Button variant="ghost" size="sm" onClick={() => navigate(`/sales/${saleId}/card-management`)}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          )}
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-serif text-2xl font-semibold">{card.viewer_name || 'Viewer Card'}</h1>
              <Badge className={statusBadgeMap[card.status] || statusBadgeMap.pending}>
                {card.status?.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground capitalize">
              {(viewerEntity?.person_industry_role || viewerEntity?.organization_industry_role || '').replace(/_/g, ' ')}
            </p>
          </div>
        </div>
        <div className="flex gap-2 self-end sm:self-auto">
          {card.status !== 'complete' && !viewOnly && (
            <Button variant="outline" size="sm" onClick={handleRequeue}>
              <RotateCcw className="w-4 h-4 mr-2" /> Re-Queue
            </Button>
          )}
        </div>
      </div>

        {/* Viewer Details */}
        <Card>
          <CardContent className="p-4">
            <ViewerDetailsEditable
              card={card}
              saleSessions={saleSessions}
              isDigitalSale={isDigitalSale}
              viewOnly={viewOnly}
              deleteSlot={!viewOnly ? (
                <button
                  onClick={handleDeleteWithConfirm}
                  disabled={deleting}
                  className="p-1 rounded hover:bg-red-50 transition-colors shrink-0"
                  title="Delete card"
                  data-testid="delete-card-btn"
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin text-red-500" /> : <Trash2 className="w-4 h-4 text-black" />}
                </button>
              ) : null}
              onUpdate={async (data) => {
                try {
                  await api.patch(`/sale-cards/${cardId}`, data);
                  invalidateCard();
                  toast.success('Card updated');
                } catch { toast.error('Failed to update card'); }
              }}
            />
          </CardContent>
        </Card>


        {/* Sale Preferences & Key Associations */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-semibold mb-2">Sale Preferences</h3>
                {viewerEntity?.sales_preferences ? (
                  <p className="text-sm">{viewerEntity.sales_preferences}</p>
                ) : (
                  <p className="text-sm text-muted-foreground">No sale preferences noted</p>
                )}
                {viewerEntity?.willing_to_buy_off_sheet && (
                  <Badge className="mt-1 bg-primary text-white text-xs py-0">Willing to buy off-sheet</Badge>
                )}
              </div>
              <div>
                <h3 className="text-sm font-semibold mb-2">Key Associations</h3>
                {(card.viewer_associations || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No associations noted</p>
                ) : (
                  <div className="space-y-1">
                    {(card.viewer_associations || []).map((a, i) => (
                      <div key={i} className="text-sm flex items-center gap-2">
                        <span className="font-medium">{a.other_entity_name}</span>
                        <span className="text-xs text-muted-foreground capitalize">{a.relationship?.replace(/_/g, ' ')}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Viewings List */}
        <Card className="border border-primary overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Viewings ({viewings.length})</CardTitle>
            {!viewOnly && (
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setAddViewingOpen(true)}>
              <Plus className="w-3 h-3 mr-1" /> Add Viewing
            </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-3 overflow-x-auto">
            {viewings.length <= 1 ? (
              viewings.map(v => (
                <ViewingRow key={v.id} viewing={v} onUpdate={updateViewing} cardStatus={card.status} viewOnly={viewOnly} />
              ))
            ) : (
              (() => {
                const groups = {};
                viewings.forEach(v => {
                  const gender = v.horse_gender || 'Other';
                  if (!groups[gender]) groups[gender] = [];
                  groups[gender].push(v);
                });
                const order = ['Colt', 'Filly', 'Mare', 'Horse', 'Gelding', 'Other'];
                const sortedKeys = Object.keys(groups).sort((a, b) => (order.indexOf(a) === -1 ? 99 : order.indexOf(a)) - (order.indexOf(b) === -1 ? 99 : order.indexOf(b)));
                return sortedKeys.map(gender => (
                  <div key={gender}>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 mt-1">{gender}s</p>
                    <div className="space-y-2">
                      {groups[gender].map(v => (
                        <ViewingRow key={v.id} viewing={v} onUpdate={updateViewing} cardStatus={card.status} viewOnly={viewOnly} />
                      ))}
                    </div>
                  </div>
                ));
              })()
            )}
          </CardContent>
        </Card>

        {/* Other Visit Notes */}
        <Card>
          <CardHeader><CardTitle className="text-base">Other Visit Notes</CardTitle></CardHeader>
          <CardContent>
            <Textarea
              placeholder="General notes about this visit..."
              defaultValue={card.other_visit_notes || ''}
              rows={3}
              onBlur={(e) => !viewOnly && updateCardNotes(e.target.value)}
              readOnly={viewOnly}
            />
          </CardContent>
        </Card>

        {/* Scanned Card Image — collapsible, starts collapsed */}
        {card.card_image_file_id && (
          <ScannedCardSection fileId={card.card_image_file_id} />
        )}

      {/* Add Viewing Dialog */}
      <Dialog open={addViewingOpen} onOpenChange={setAddViewingOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add Viewing</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">Select a horse to add to this card.</p>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {opportunities.filter(opp => !(viewings || []).some(v => v.sale_opportunity_id === opp.id)).map(opp => (
                <div key={opp.id} className="flex items-center justify-between p-2 border rounded hover:bg-muted/50 cursor-pointer" onClick={async () => {
                  try {
                    await api.post(`/sale-cards/${cardId}/add-viewing`, { sale_opportunity_id: opp.id });
                    toast.success('Viewing added');
                    setAddViewingOpen(false);
                    invalidateCard();
                  } catch { toast.error('Failed to add viewing'); }
                }}>
                  <p className="text-sm font-medium">#{opp.hip_number || 'TBD'} - {opp.opportunity_name?.split(' - ')[1] || opp.opportunity_name}</p>
                </div>
              ))}
              {opportunities.filter(opp => !(viewings || []).some(v => v.sale_opportunity_id === opp.id)).length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">All horses are already on this card.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddViewingOpen(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* --- Viewing Row --- */
const ViewingRow = ({ viewing, onUpdate, cardStatus, viewOnly }) => {
  const isEditable = !viewOnly && cardStatus !== 'complete';
  const [editingTime, setEditingTime] = useState(false);
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');

  const openTimeEditor = (e) => {
    e.stopPropagation();
    const ts = viewing.updated_at ? new Date(viewing.updated_at) : new Date();
    setEditDate(ts.toLocaleDateString('sv')); // YYYY-MM-DD local
    setEditTime(ts.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }));
    setEditingTime(true);
  };

  const saveTime = (e) => {
    e.stopPropagation();
    if (!editDate) { setEditingTime(false); return; }
    const combined = new Date(`${editDate}T${editTime || '00:00'}:00`);
    if (isNaN(combined.getTime())) { toast.error('Invalid date/time'); return; }
    onUpdate(viewing.id, { updated_at: combined.toISOString() });
    setEditingTime(false);
  };

  const toggleField = (field) => {
    if (!isEditable) return;
    onUpdate(viewing.id, { [field]: !viewing[field] });
  };

  const setStatus = (status) => {
    onUpdate(viewing.id, { status });
  };

  const tickedAt = viewing.updated_at && viewing.status !== 'pending'
    ? new Date(viewing.updated_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : null;

  return (
    <div className={`flex items-center gap-4 p-3 border rounded min-w-[560px] ${viewing.status === 'completed' ? 'bg-green-50/50' : viewing.status === 'cancelled' ? 'bg-muted/50' : ''}`}>
      {/* Hip & Horse */}
      <div className="w-16 text-sm font-medium">#{viewing.hip_number || 'TBD'}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{viewing.horse_name || '-'}</p>
        <p className="text-xs text-muted-foreground">
          {[viewing.horse_age, viewing.horse_gender]
            .filter(Boolean).join(' · ')}
          {viewing.horse_gender && ['Filly', 'Mare'].includes(viewing.horse_gender) && viewing.cover_status === 'in_foal' && (
            ' · In Foal'
          )}
        </p>
        {viewing.sold_as && (
          <p className="text-xs text-muted-foreground">
            Sold As: {formatSoldAs(viewing.sold_as)}
          </p>
        )}
        {(viewing.flags || []).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {viewing.flags.map((flag, i) => (
              <Badge key={i} variant="destructive" className="text-xs py-0 px-1.5">{flag}</Badge>
            ))}
          </div>
        )}
      </div>

      {/* Liked */}
      <Button
        variant="ghost" size="sm"
        className={`h-8 w-8 p-0 ${viewing.liked ? 'text-red-500' : 'text-muted-foreground'}`}
        onClick={() => toggleField('liked')}
        title="Liked"
        disabled={!isEditable}
      >
        <Heart className={`w-4 h-4 ${viewing.liked ? 'fill-current' : ''}`} />
      </Button>

      {/* Viewed Vet Report */}
      <Button
        variant="ghost" size="sm"
        className={`h-8 w-8 p-0 ${viewing.viewed_vet_report ? 'text-blue-500' : 'text-muted-foreground'}`}
        onClick={() => toggleField('viewed_vet_report')}
        title="Viewed Vet Report"
        disabled={!isEditable}
      >
        <FileText className={`w-4 h-4 ${viewing.viewed_vet_report ? 'fill-blue-100' : ''}`} />
      </Button>

      {/* Notes */}
      <input
        className="flex-1 max-w-xs h-8 px-2 text-xs border rounded bg-background"
        placeholder="Notes..."
        defaultValue={viewing.notes || ''}
        onBlur={(e) => { if (e.target.value !== (viewing.notes || '')) onUpdate(viewing.id, { notes: e.target.value }); }}
        disabled={!isEditable}
      />

      {/* Status Buttons */}
      {viewing.status === 'pending' && !viewOnly && (
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-green-600 hover:bg-green-50" onClick={() => setStatus('completed')} title="Complete">
            <Check className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-50" onClick={() => setStatus('cancelled')} title="Cancel">
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}
      {viewing.status !== 'pending' && (
        <div className="flex flex-col items-end gap-0.5">
          <div className="flex items-center gap-1">
            <Badge className={viewing.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
              {viewing.status === 'completed' ? 'Done' : 'Cancelled'}
            </Badge>
            {/* Clock icon — always visible for non-pending viewings */}
            {!viewOnly && (
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground hover:text-primary" onClick={openTimeEditor} title="Edit viewed date/time">
                <Clock className="w-3 h-3" />
              </Button>
            )}
            {!viewOnly && (
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground hover:text-primary" onClick={() => setStatus('pending')} title="Undo">
              <RotateCcw className="w-3 h-3" />
            </Button>
            )}
          </div>
          {tickedAt && !editingTime && (
            <span className="text-[10px] text-foreground">{tickedAt}</span>
          )}
          {/* Inline date/time editor */}
          {editingTime && (
            <div className="flex items-center gap-1 mt-1" onClick={e => e.stopPropagation()}>
              <input
                type="date"
                value={editDate}
                onChange={e => setEditDate(e.target.value)}
                className="h-6 text-xs border rounded px-1 bg-background w-28"
                style={{ color: '#111827', WebkitTextFillColor: '#111827' }}
              />
              <input
                type="time"
                value={editTime}
                onChange={e => setEditTime(e.target.value)}
                className="h-6 text-xs border rounded px-1 bg-background w-20"
                style={{ color: '#111827', WebkitTextFillColor: '#111827' }}
              />
              <Button size="sm" className="h-6 px-2 text-xs bg-primary hover:bg-primary/90 text-white" onClick={saveTime}>Save</Button>
              <Button size="sm" variant="ghost" className="h-6 px-1 text-xs" onClick={e => { e.stopPropagation(); setEditingTime(false); }}>✕</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* --- Viewer Details Editable --- */
const INDUSTRY_ROLES = [
  { value: 'owner', label: 'Owner' },
  { value: 'trainer', label: 'Trainer' },
  { value: 'agent', label: 'Agent' },
  { value: 'farm_manager', label: 'Farm Manager' },
  { value: 'veterinarian', label: 'Veterinarian' },
  { value: 'consignor', label: 'Consignor' },
  { value: 'syndicate_member', label: 'Syndicate Member' },
  { value: 'bloodstock_agent', label: 'Bloodstock Agent' },
  { value: 'jockey', label: 'Jockey' },
  { value: 'other', label: 'Other' },
];

const ViewerDetailsEditable = ({ card, saleSessions, isDigitalSale, viewOnly, onUpdate, deleteSlot }) => {
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState(card.date ? card.date.split('T')[0] : '');
  const [time, setTime] = useState(card.time || '');
  const [selectedSessions, setSelectedSessions] = useState(new Set(card.session_dates || []));

  // Viewer name / CRM entity editing
  const [viewerName, setViewerName] = useState(card.viewer_name || '');
  const [viewerEntityId, setViewerEntityId] = useState(card.viewer_crm_entity_id || '');
  const [viewerRole, setViewerRole] = useState('');
  const [viewerSearch, setViewerSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [quickCreate, setQuickCreate] = useState(false);
  const [newType, setNewType] = useState('person');
  const [newFirst, setNewFirst] = useState('');
  const [newLast, setNewLast] = useState('');
  const [newOrgName, setNewOrgName] = useState('');
  const [newRole, setNewRole] = useState('');
  const [saving, setSaving] = useState(false);
  const _searchTimerCV = useRef(null);

  useEffect(() => {
    setDate(card.date ? card.date.split('T')[0] : '');
    setTime(card.time || '');
    setSelectedSessions(new Set(card.session_dates || []));
    setViewerName(card.viewer_name || '');
    setViewerEntityId(card.viewer_crm_entity_id || '');
  }, [card]);

  // Fetch contacts + current role when editing opens
  useEffect(() => {
    if (!editing) return;
    api.get('/crm-entities').then(r => setAllContacts(r.data || [])).catch(() => {});
    if (card.viewer_crm_entity_id) {
      api.get(`/crm-entities/${card.viewer_crm_entity_id}`).then(r => {
        const e = r.data;
        setViewerRole(e?.person_industry_role || e?.organization_industry_role || '');
      }).catch(() => {});
    }
  }, [editing, card.viewer_crm_entity_id]);

  const searchCRM = (q) => {
    if (!q.trim()) { setSearchResults([]); return; }
    clearTimeout(_searchTimerCV.current);
    _searchTimerCV.current = setTimeout(async () => {
      try {
        const res = await api.get(`/crm-entities?search=${encodeURIComponent(q)}`);
        setSearchResults(res.data.slice(0, 8));
      } catch { setSearchResults([]); }
    }, 200);
  };

  const handleQuickCreate = async () => {
    const isPerson = newType === 'person';
    const displayName = isPerson ? `${newFirst} ${newLast}`.trim() : newOrgName.trim();
    if (isPerson && (!newFirst || !newLast)) { toast.error('First and last name required'); return; }
    if (!isPerson && !newOrgName) { toast.error('Organization name required'); return; }
    const dup = allContacts.find(c => c.system_name?.toLowerCase() === displayName.toLowerCase());
    if (dup) { setViewerEntityId(dup.id); setViewerName(dup.system_name); setViewerRole(dup.person_industry_role || dup.organization_industry_role || ''); setQuickCreate(false); toast.error(`"${dup.system_name}" already exists — selected.`); return; }
    setSaving(true);
    try {
      const payload = isPerson
        ? { crm_type: 'person', crm_class: 'contact', person_first_name: newFirst, person_last_name: newLast, person_industry_role: newRole || null, system_name: displayName }
        : { crm_type: 'organization', crm_class: 'contact', organization_name: newOrgName, organization_industry_role: newRole || null, system_name: displayName };
      const res = await api.post('/crm-entities', payload);
      setViewerEntityId(res.data.id);
      setViewerName(displayName);
      setViewerRole(newRole || '');
      setAllContacts(prev => [...prev, res.data]);
      setQuickCreate(false);
      toast.success(`${isPerson ? 'Contact' : 'Organization'} created`);
    } catch { toast.error('Failed to create'); }
    setSaving(false);
  };

  const toggleSession = (name) => {
    setSelectedSessions(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const handleSave = async () => {
    // If role changed and we have an entity, update it
    if (viewerEntityId && viewerRole) {
      await api.patch(`/crm-entities/${viewerEntityId}`, { person_industry_role: viewerRole }).catch(() => {});
    }
    onUpdate({
      date: date || null,
      time: time || null,
      session_dates: Array.from(selectedSessions),
      viewer_name: viewerName || card.viewer_name,
      viewer_crm_entity_id: viewerEntityId || card.viewer_crm_entity_id || null,
    });
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="space-y-4">
        {/* Viewer Name */}
        <div className="space-y-2">
          <Label className="text-xs font-semibold">Viewer Name</Label>
          {!quickCreate ? (
            <div className="space-y-1.5">
              <div className="relative">
                <Input
                  className="pr-3"
                  placeholder="Search by name..."
                  value={viewerEntityId ? viewerName : viewerSearch}
                  onChange={e => {
                    if (viewerEntityId) { setViewerEntityId(''); setViewerName(''); }
                    setViewerSearch(e.target.value);
                    searchCRM(e.target.value);
                  }}
                />
              </div>
              {searchResults.length > 0 && !viewerEntityId && (
                <div className="border rounded max-h-28 overflow-y-auto">
                  {searchResults.map(e => (
                    <div key={e.id} className="p-2 text-sm hover:bg-muted/50 cursor-pointer"
                      onClick={async () => { setViewerEntityId(e.id); setViewerName(e.system_name); setViewerRole(e.person_industry_role || e.organization_industry_role || ''); setViewerSearch(''); setSearchResults([]); }}>
                      {e.system_name}
                      {(e.person_industry_role || e.organization_industry_role) && (
                        <span className="text-xs text-muted-foreground ml-1 capitalize">
                          {(e.person_industry_role || e.organization_industry_role).replace(/_/g, ' ')}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {viewerEntityId && <Badge variant="outline" className="text-xs">Linked: {viewerName}</Badge>}
              <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => setQuickCreate(true)}>
                + Create New Contact
              </Button>
            </div>
          ) : (
            <div className="space-y-2 p-3 border rounded">
              <div className="flex gap-1">
                <Button size="sm" type="button" variant={newType === 'person' ? 'default' : 'outline'} className="h-7 text-xs flex-1" onClick={() => setNewType('person')}>Person</Button>
                <Button size="sm" type="button" variant={newType === 'organization' ? 'default' : 'outline'} className="h-7 text-xs flex-1" onClick={() => setNewType('organization')}>Organization</Button>
              </div>
              {newType === 'person' ? (
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="First Name *" value={newFirst} onChange={e => setNewFirst(e.target.value)} />
                  <Input placeholder="Last Name *" value={newLast} onChange={e => setNewLast(e.target.value)} />
                </div>
              ) : (
                <Input placeholder="Organization Name *" value={newOrgName} onChange={e => setNewOrgName(e.target.value)} />
              )}
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger><SelectValue placeholder="Industry Role (optional)" /></SelectTrigger>
                <SelectContent>{INDUSTRY_ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleQuickCreate} disabled={saving} className="bg-primary hover:bg-primary/90 text-white">Create</Button>
                <Button size="sm" variant="outline" onClick={() => setQuickCreate(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </div>

        {/* Industry Role (only shown when linked to a contact) */}
        {viewerEntityId && (
          <div className="space-y-1">
            <Label className="text-xs font-semibold">Industry Role</Label>
            <Select value={viewerRole} onValueChange={setViewerRole}>
              <SelectTrigger><SelectValue placeholder="Select role..." /></SelectTrigger>
              <SelectContent>{INDUSTRY_ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}

        {/* Date / Time */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Date</Label>
            <Input type="date" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Time</Label>
            <TimeSelect value={time} onChange={setTime} />
          </div>
        </div>
        {!isDigitalSale && (
        <div className="space-y-1">
          <Label className="text-xs">Session Date(s)</Label>
          {saleSessions.length === 0 ? (
            <p className="text-xs text-muted-foreground">No sessions available for this sale</p>
          ) : (
            <div className="space-y-1 border rounded p-2 max-h-28 overflow-y-auto">
              {saleSessions.sort((a, b) => new Date(a.date) - new Date(b.date)).map(s => (
                <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/30 p-1 rounded">
                  <Checkbox
                    checked={selectedSessions.has(s.name)}
                    onCheckedChange={() => toggleSession(s.name)}
                  />
                  <span>{s.name}</span>
                  <span className="text-xs text-muted-foreground ml-auto">
                    {s.date ? new Date(String(s.date).split('T')[0] + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
        )}
        <div className="flex items-center justify-between">
          {deleteSlot ? <div>{deleteSlot}</div> : <div />}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={async () => {
              setEditing(false);
              setQuickCreate(false);
              setViewerName(card.viewer_name || '');
              setViewerEntityId(card.viewer_crm_entity_id || '');
              setViewerRole('');
              setViewerSearch('');
              setSearchResults([]);
            }}>Cancel</Button>
            <Button size="sm" className="bg-primary hover:bg-primary/90 text-white" onClick={handleSave}>Save</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
        <div>
          <span className="text-muted-foreground">Date: </span>
          <span className="font-medium">{card.date ? card.date.split('T')[0].replace(/(\d{4})-(\d{2})-(\d{2})/, (_, y, m, d) => `${parseInt(m)}/${parseInt(d)}/${y}`) : '-'}</span>
        </div>
        {card.time && (
          <div>
            <span className="text-muted-foreground">Time: </span>
            <span className="font-medium">{card.time}</span>
          </div>
        )}
        {!isDigitalSale && card.session_dates?.length > 0 && (
          <div>
            <span className="text-muted-foreground">Sessions: </span>
            <span className="font-medium">{card.session_dates.join(', ')}</span>
          </div>
        )}
        {card.card_type !== 'individual' && (
          <Badge variant="outline" className="capitalize">{card.card_type.replace(/_/g, ' ')}</Badge>
        )}
      </div>
      {!viewOnly && (
      <Button variant="ghost" size="icon" onClick={() => setEditing(true)}>
        <Pencil className="w-4 h-4" />
      </Button>
      )}
    </div>
  );
};

/* --- Scanned Card Section (collapsible) --- */
const ScannedCardSection = ({ fileId }) => {
  const [open, setOpen] = useState(false);
  return (
    <Card>
      <CardHeader className="cursor-pointer py-3" onClick={() => setOpen(!open)}>
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <CardTitle className="text-base">Scanned Card</CardTitle>
        </div>
      </CardHeader>
      {open && (
        <CardContent className="pt-0">
          <div className="border rounded overflow-hidden bg-muted/20 flex items-center justify-center">
            <img
              src={`${window.location.origin}/api/files/public/${fileId}`}
              alt="Scanned viewing card"
              className="max-w-full max-h-[400px] object-contain"
            />
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default CardViewPage;
